import { useEffect, useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, MessageCircle, ShieldCheck, TriangleAlert } from "lucide-react";
import useSWRMutation from "swr/mutation";

import {
  apiClient,
  messagingEndpoint,
  type MessagingChannel,
  type MessagingProvider,
  type MessagingSetupInspectionResponse,
} from "@/api/client";
import Footer from "@/components/Footer";
import MagicLinkLogin from "@/components/MagicLinkLogin";
import SharedHeader from "@/components/SharedHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useMessaging } from "@/hooks/useMessaging";
import { API_BASE_URL } from "@/lib/env";
import {
  getMessagingSetupToken,
  getMessagingConnection,
  getSafeTelegramEntryURL,
  getSafeWhatsAppEntryURL,
  getSafeMessagingIdentityMetadata,
  getMessagingConnectionErrorMessage,
  getMessagingState,
  stripMessagingSetupToken,
  SIGNAL_CONSENT_VERSION,
  SIGNAL_E2E_CONSENT_VERSION,
  TELEGRAM_CONSENT_VERSION,
  WHATSAPP_CONSENT_VERSION,
  WHATSAPP_REGION_AVAILABILITY,
} from "@/lib/messaging";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

function formatExpiry(value: string | null | undefined): string | null {
  if (!value) return null;
  const expiresAt = new Date(value);
  if (!Number.isFinite(expiresAt.getTime())) return null;
  return expiresAt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function resolveSetupChannel(
  channel: MessagingChannel | null | undefined,
  provider: MessagingProvider | null | undefined,
): MessagingChannel {
  if (channel) return channel;
  if (provider === "signal" || provider === "signal_e2e") return "signal";
  if (provider === "telegram_bot_api") return "telegram";
  return "whatsapp";
}

export default function TextConnectPage() {
  const {
    clearPendingMessagingSetup,
    isAuthenticated,
    pendingMessagingSetup,
    user,
  } = useAuth();
  const [setupToken, setSetupToken] = useState<string | null>(() => (
    typeof window === "undefined" ? null : getMessagingSetupToken(window.location.href)
  ));
  const [isLocationScrubbed, setIsLocationScrubbed] = useState(false);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [linkComplete, setLinkComplete] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const { messagingStatus, isLoading: isStatusLoading, refreshMessaging } = useMessaging(isAuthenticated);

  const {
    data: inspection,
    error: inspectionError,
    isMutating: isInspecting,
    trigger: inspectSetup,
  } = useSWRMutation<MessagingSetupInspectionResponse, Error, string, string>(
    messagingEndpoint(API_BASE_URL, "inspect"),
    (_key, { arg }) => apiClient.inspectMessagingSetup(arg),
  );
  const incomingIdentity = inspection ?? pendingMessagingSetup;
  const currentConnection = getMessagingConnection(messagingStatus);
  const provider = incomingIdentity?.provider ?? currentConnection?.provider ?? "signal";
  const setupChannel = resolveSetupChannel(inspection?.channel, provider);
  const isSignal = provider === "signal" || provider === "signal_e2e" || setupChannel === "signal";
  const isTelegram = provider === "telegram_bot_api" || setupChannel === "telegram";
  const connectionName = isSignal ? "Signal" : isTelegram ? "Telegram" : "WhatsApp";
  const connectionState = getMessagingState(messagingStatus, setupChannel);
  const isUnlinkPending = connectionState === "unlinking";
  const isAlreadyLinked = connectionState === "linked" || connectionState === "opted_out" || isUnlinkPending;

  useLayoutEffect(() => {
    const referrerMeta = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
    const previousReferrerPolicy = referrerMeta?.content ?? null;
    const meta = referrerMeta ?? document.createElement("meta");
    if (!referrerMeta) {
      meta.name = "referrer";
      document.head.appendChild(meta);
    }
    meta.content = "no-referrer";

    if (setupToken) {
      window.history.replaceState(
        window.history.state,
        "",
        stripMessagingSetupToken(window.location.href),
      );
    }
    setIsLocationScrubbed(true);

    return () => {
      if (previousReferrerPolicy === null) {
        meta.remove();
      } else {
        meta.content = previousReferrerPolicy;
      }
    };
  }, [setupToken]);

  useEffect(() => {
    if (!isLocationScrubbed || !setupToken) return;
    void inspectSetup(setupToken).catch(() => undefined);
  }, [inspectSetup, isLocationScrubbed, setupToken]);

  const handleLink = async () => {
    if ((!setupToken && !pendingMessagingSetup) || !consentConfirmed) return;
    setIsLinking(true);
    setLinkError(null);

    try {
      await apiClient.linkMessaging(setupChannel, setupToken
        ? {
            setup_token: setupToken,
            consent: true,
            consent_version: provider === "signal"
              ? SIGNAL_CONSENT_VERSION
              : provider === "signal_e2e"
                ? SIGNAL_E2E_CONSENT_VERSION
                : provider === "telegram_bot_api"
                  ? TELEGRAM_CONSENT_VERSION
              : WHATSAPP_CONSENT_VERSION,
          }
        : {
            auth_continuation: pendingMessagingSetup!.token,
            consent: true,
            consent_version: provider === "signal"
              ? SIGNAL_CONSENT_VERSION
              : provider === "signal_e2e"
                ? SIGNAL_E2E_CONSENT_VERSION
                : provider === "telegram_bot_api"
                  ? TELEGRAM_CONSENT_VERSION
              : WHATSAPP_CONSENT_VERSION,
          });
      setSetupToken(null);
      clearPendingMessagingSetup();
      setLinkComplete(true);
      await refreshMessaging();
    } catch (error) {
      setLinkError(getMessagingConnectionErrorMessage(error, provider));
    } finally {
      setIsLinking(false);
    }
  };

  const entryURL = isSignal
    ? null
    : isTelegram
      ? getSafeTelegramEntryURL(messagingStatus?.telegram_entry_url)
      : getSafeWhatsAppEntryURL(messagingStatus?.whatsapp_entry_url);
  const subscriptionActive = messagingStatus?.eligibility?.subscription_active
    ?? user?.subscription_status === "active";
  const validInspection = inspection?.valid === true
    && (
      !inspection.channel ||
      inspection.channel === "signal" ||
      inspection.channel === "telegram" ||
      inspection.channel === "whatsapp"
    );
  const { countryCode, identityLabel } = getSafeMessagingIdentityMetadata(incomingIdentity);
  const hasIncomingCapability = Boolean(setupToken || pendingMessagingSetup);
  const showConnected = linkComplete
    || ((connectionState === "linked" || connectionState === "opted_out") && !hasIncomingCapability);

  let content: React.ReactNode;

  if (showConnected) {
    content = (
      <div className="text-center">
        <div className={cn(theme.colors.warmYellow, theme.borders.neubrutalism, "mx-auto flex h-20 w-20 items-center justify-center rounded-2xl")}>
          <CheckCircle2 className="h-10 w-10 text-gray-900 dark:text-white" aria-hidden="true" />
        </div>
        <h1 className="mt-7 text-3xl font-black text-gray-900 dark:text-white sm:text-4xl">
          {connectionName} is connected
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-gray-600 dark:text-gray-300">
          {subscriptionActive
            ? isSignal
              ? "Return to Signal and ask Podwise about any podcast you follow. Your first retained question will continue automatically after setup."
              : `Return to ${connectionName} and ask Podwise about any podcast you follow.`
            : `${connectionName} is connected. Continue now to choose a Podwise plan, accept the terms, and finish checkout.`}
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          {entryURL ? (
            <a
              href={entryURL}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="no-referrer"
              className={cn(theme.buttons.primary, "inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold")}
            >
              Return to {connectionName}
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          ) : null}
          <Link
            to={subscriptionActive ? "/profile" : "/paywall"}
            className={cn(
              subscriptionActive ? theme.buttons.secondary : theme.buttons.primary,
              "inline-flex items-center justify-center rounded-xl px-6 py-3 font-semibold",
            )}
          >
            {subscriptionActive ? "View Settings" : "Continue to plans"}
          </Link>
        </div>
      </div>
    );
  } else if (isAuthenticated && isStatusLoading) {
    content = (
      <div className="flex flex-col items-center py-12 text-center text-gray-600 dark:text-gray-300" role="status">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-primary-orange dark:border-gray-600 dark:border-t-primary-orange-dark" aria-hidden="true" />
        <p className="mt-4 font-medium">Checking your messaging connection…</p>
      </div>
    );
  } else if (!setupToken && !pendingMessagingSetup) {
    content = (
      <div className="text-center">
        <TriangleAlert className="mx-auto h-12 w-12 text-primary-orange-dark dark:text-primary-orange" aria-hidden="true" />
        <h1 className="mt-5 text-3xl font-black text-gray-900 dark:text-white">Setup link unavailable</h1>
        <p className="mx-auto mt-4 max-w-lg text-base leading-7 text-gray-600 dark:text-gray-300">
          This private setup link is missing or has already left this browser. Return to your Podwise messaging conversation and request a new link.
        </p>
        <Link
          to={isAuthenticated ? "/profile" : "/"}
          className={cn(theme.buttons.secondary, "mt-7 inline-flex rounded-xl px-6 py-3 font-semibold")}
        >
          {isAuthenticated ? "Open Settings" : "Go to Podwise"}
        </Link>
      </div>
    );
  } else if (setupToken && (!isLocationScrubbed || isInspecting)) {
    content = (
      <div className="flex flex-col items-center py-12 text-center text-gray-600 dark:text-gray-300" role="status">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-primary-orange dark:border-gray-600 dark:border-t-primary-orange-dark" aria-hidden="true" />
        <p className="mt-4 font-medium">Checking your private messaging setup link…</p>
      </div>
    );
  } else if (setupToken && (inspectionError || !validInspection)) {
    content = (
      <div className="text-center">
        <TriangleAlert className="mx-auto h-12 w-12 text-primary-orange-dark dark:text-primary-orange" aria-hidden="true" />
        <h1 className="mt-5 text-3xl font-black text-gray-900 dark:text-white">Request a new setup link</h1>
        <p className="mx-auto mt-4 max-w-lg text-base leading-7 text-gray-600 dark:text-gray-300">
          This link is invalid, expired, or already used. For your security, setup links work only once.
        </p>
      </div>
    );
  } else if (isAuthenticated && isAlreadyLinked && hasIncomingCapability) {
    content = (
      <div className="text-center">
        <TriangleAlert className="mx-auto h-12 w-12 text-primary-orange-dark dark:text-primary-orange" aria-hidden="true" />
        <h1 className="mt-5 text-3xl font-black text-gray-900 dark:text-white">
          {isUnlinkPending ? `${connectionName} unlink is still finishing` : `${connectionName} is already connected`}
        </h1>
        <p className="mx-auto mt-4 max-w-lg text-base leading-7 text-gray-600 dark:text-gray-300">
          {isUnlinkPending
            ? `Podwise will not replace this ${connectionName} identity until the current unlink has completed. Check Settings, then request a fresh setup link.`
            : `This Podwise account already has a ${connectionName} identity connected. For safety, Podwise will not replace it from an incoming setup link. Unlink the current connection in Settings, then request a fresh link.`}
        </p>
        <Link
          to="/profile"
          className={cn(theme.buttons.secondary, "mt-7 inline-flex rounded-xl px-6 py-3 font-semibold")}
        >
          Open Settings
        </Link>
      </div>
    );
  } else if (!isAuthenticated) {
    content = (
      <MagicLinkLogin
        embedded
        messagingSetup={setupToken ?? undefined}
        title={`Sign in to connect ${connectionName}`}
        description={`We'll email you a private sign-in link and return you to this ${connectionName} connection.`}
      />
    );
  } else {
    content = (
      <div>
        <div className="flex items-start gap-4">
          <div className={cn(theme.colors.warmYellow, "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl")}>
            <ShieldCheck className="h-6 w-6 text-gray-900 dark:text-white" aria-hidden="true" />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Private account link
            </p>
            <h1 className="mt-1 text-3xl font-black text-gray-900 dark:text-white">Connect {connectionName}</h1>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Connect this {connectionName} identity to <strong>{user?.email}</strong>.
              {formatExpiry(inspection?.expires_at ?? pendingMessagingSetup?.expires_at)
                ? ` This confirmation expires ${formatExpiry(inspection?.expires_at ?? pendingMessagingSetup?.expires_at)}.`
                : ""}
            </p>
            {identityLabel || countryCode ? (
              <dl className="mt-3 grid gap-2 text-sm text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                {identityLabel ? (
                  <div>
                    <dt className="font-medium text-gray-500 dark:text-gray-400">{connectionName} identity</dt>
                    <dd className="mt-1 text-gray-900 dark:text-gray-100">{identityLabel}</dd>
                  </div>
                ) : null}
                {countryCode ? (
                  <div>
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Registration country</dt>
                    <dd className="mt-1 text-gray-900 dark:text-gray-100">{countryCode}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
          </div>
        </div>

        <div className={cn(theme.colors.warmYellowBg, "mt-7 rounded-xl border-2 border-gray-900 p-5 dark:border-gray-300")}>
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-6 text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={consentConfirmed}
              onChange={event => setConsentConfirmed(event.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 accent-primary-orange-dark"
            />
            <span>
              {isSignal
                ? "I agree to receive Podwise service messages through this Signal account. I understand that Podwise will process my questions through Signal, Cloudflare, and its AI providers; answer requests count toward my existing chat usage; message contents and protected routing data follow the retention policy; the connection lasts until I unlink it; and I can send STOP or unlink at any time."
                : isTelegram
                  ? "I agree to receive Podwise service messages through this Telegram account. I understand that Podwise will process my questions through Telegram, Cloudflare, and its AI providers; answer requests count toward my existing chat usage; message contents and protected routing data follow the retention policy; the connection lasts until I unlink it; and I can send STOP or unlink at any time."
                : "I agree to receive Podwise service messages through this WhatsApp account. I understand that Podwise will process my questions through Twilio, Meta, and its AI providers; answer requests and messaging transport count toward my existing chat usage; message contents and protected routing data are scheduled for redaction after 7 days, while body-free delivery, billing, consent, and security records may remain for up to 1 year; protected conversation memory may retain up to three recent question-and-answer excerpts for 30 days; the active WhatsApp connection lasts until I unlink it; and I can send STOP or unlink at any time."}
            </span>
          </label>
        </div>

        {linkError ? (
          <div className={cn(theme.borders.error, theme.colors.errorBg, theme.text.error, "mt-5 rounded-xl p-4 text-sm")} role="alert">
            {linkError}
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => void handleLink()}
          disabled={!consentConfirmed || isLinking}
          className={cn(
            theme.buttons.primary,
            "mt-6 inline-flex w-full items-center justify-center rounded-xl px-6 py-3 font-semibold",
            (!consentConfirmed || isLinking) && "cursor-not-allowed opacity-60",
          )}
        >
          {isLinking ? "Connecting…" : `Agree and connect ${connectionName}`}
        </button>

        <p className="mt-5 text-center text-xs leading-5 text-gray-500 dark:text-gray-400">
          Only podcasts you follow are available. Review the{" "}
          <Link to="/privacy" className="font-semibold underline underline-offset-2">Privacy Policy</Link>{" "}
          and{" "}
          <Link to="/terms" className="font-semibold underline underline-offset-2">Terms</Link>.
        </p>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}>
      <SharedHeader />
      <main className="mx-auto flex w-full max-w-3xl flex-1 items-center px-4 py-28 sm:px-6">
        <section className={cn(theme.cards.base, "w-full p-6 sm:p-10")}>
          <div className="mb-8 flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            Podwise on {connectionName}
          </div>
          {content}
          <p className="mt-8 border-t-2 border-gray-300 pt-5 text-center text-sm font-medium leading-6 text-gray-700 dark:border-gray-600 dark:text-gray-200">
            {isSignal
              ? "Your Signal identity is converted to an opaque routing identifier before Podwise research begins. Never send passwords, sign-in codes, or payment-card details in Signal."
              : isTelegram
                ? "Your Telegram user ID is converted to an opaque routing identifier before Podwise research begins. Never send passwords, sign-in codes, or payment-card details in Telegram."
              : WHATSAPP_REGION_AVAILABILITY}
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
